import { stillOwned, type WineListRow } from "@corklog/shared";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { fetchWines } from "../api.js";
import { useMayCarry } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { MealSuggestion } from "../components/MealSuggestion.js";
import { OwnersOnly } from "../components/OwnersOnly.js";
import { SwitchedOff } from "../components/SwitchedOff.js";
import { useReload } from "../offline.js";
import { useFeatures } from "../session.js";

/**
 * *Till maten* — I am cooking this; which bottle should I open?
 *
 * **A section of its own, since 2026-09-18.** Task `10.1` put this on *Att
 * öppna* on the argument that the meal is one more filter on a question that
 * page already asks. Driving it proved the argument wrong in the place it
 * matters: the answer is drawn with `WineRow`, the urgency groups below it are
 * drawn with `WineRow`, and the two lists look identical while saying opposite
 * things — *this suits tonight's dinner* and *this is running out whatever you
 * are cooking*. A wine appearing in the second list for the first list's
 * reason is the page lying about why a bottle is on it. The decision and its
 * reversal are both in `project-scope.md`.
 *
 * **The row of sections stayed nine long.** *Inventering* left it for the same
 * treatment *Till serveringen* and *Orden* already have — reached from
 * *Luckor*, which is the same errand with a different question — so this took
 * a place rather than adding a tenth to a row that wraps onto three lines on a
 * phone.
 *
 * **The list is fetched here, and the answer is drawn from it.** The model
 * replies with wine ids; the producer, the vintage, the band and — the half
 * that makes the advice actionable before dinner — which cabinet and which
 * shelf all come out of the cellar's own record. A wine the model invented
 * cannot appear, and the shelf cannot be got wrong.
 *
 * **Owners only, because asking spends money.** The same `write` role the
 * route requires: a guest may read this cellar and may not spend its owner's
 * money on it.
 */
const CURRENT_YEAR = new Date().getFullYear();

type State =
  | { status: "loading" }
  | { status: "ready"; wines: WineListRow[] }
  | { status: "failed" };

export function Meal() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const mayCarry = useMayCarry();
  const { meal } = useFeatures();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(() => {
    void fetchWines(cellarId)
      .then((wines) => setState({ status: "ready", wines }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  // A guest has no link here — the row of sections stopped drawing it at `9.2`
  // — but a typed address still arrives, and the answer behind it is a bill
  // somebody else pays. A contributor is shown it: the question is what to
  // open with this, asked by somebody who is about to open it.
  if (mayCarry === false) {
    return (
      <section>
        <CellarHeading cellarId={cellarId} current="meal" />
        <OwnersOnly cellarId={cellarId} />
      </section>
    );
  }

  // The administrator's switch, since 2026-09-28. The link left the row with
  // it; this is the answer to an address typed or kept from before.
  if (!meal) {
    return (
      <section>
        <CellarHeading cellarId={cellarId} current="meal" />
        <SwitchedOff cellarId={cellarId} />
      </section>
    );
  }

  const owned = state.status === "ready" ? state.wines.filter(stillOwned) : [];

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="meal" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("meal.intro")}</p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("wines.loadFailed")}
        </p>
      )}

      {/* The form waits for the list, because every row it draws comes out of
          it — and it is withheld from a cellar with no bottles in it, where
          asking what suits dinner is a form that can only be answered
          *nothing*, half a minute and one paid call later. */}
      {state.status === "ready" &&
        (owned.length === 0 ? (
          <p className="text-muted mt-8 max-w-prose text-sm">{t("meal.emptyCellar")}</p>
        ) : (
          <MealSuggestion
            cellarId={cellarId}
            wines={state.wines}
            currentYear={CURRENT_YEAR}
          />
        ))}
    </section>
  );
}
